import { useEffect, useId, useRef } from 'react'
import { useGatewayBeneficiaries } from '../../api/queries'
import type { Biller, BeneficiaryLine, GatewayEntry } from '../../api/types'
import { useT } from '../../i18n'
import { Button, DataTable, EmptyState, IconButton, SelectField, Skeleton, type Column } from '../../ui'
import panel from '../transactions/transactions.module.css'
import styles from './gateways.module.css'
import { apiErrorText } from '../../bff/errorText'

export interface BeneficiariesPanelProps {
  readonly gateway: GatewayEntry
  /** Beneficiary organisations only — a biller can never hold a slice. */
  readonly organisations: readonly Biller[]
  readonly busy: string | null
  readonly onAssign: (line: BeneficiaryLine, code: string) => void
  readonly onClose: () => void
}

/**
 * The beneficiaries of one gateway (V1.1, FR-42): every identity the
 * warehouse has recorded on it — "seen in data" — and who holds each slice,
 * the same observed → assigned → revoked lifecycle the gateway row has. The
 * identities are internal-only, which is why this lives on the internal
 * mapping screen and nowhere else.
 */
export function BeneficiariesPanel({ gateway, organisations, busy, onAssign, onClose }: BeneficiariesPanelProps) {
  const t = useT()
  const headingId = useId()
  const ref = useRef<HTMLElement>(null)
  const data = useGatewayBeneficiaries(gateway.gateway_id)
  // One identity per gateway per organisation: a row may keep its own
  // holder and take any organisation holding nothing here; an organisation
  // holding another identity on this gateway is out.
  const holding = new Set((data.data?.beneficiaries ?? []).map((l) => l.biller_id).filter(Boolean))
  const offeredFor = (line: BeneficiaryLine) => organisations.filter((o) => o.biller_id === line.biller_id || !holding.has(o.biller_id))

  // Focus once on mount; the parent keys this panel on the gateway, so a
  // different gateway is a fresh mount and focus moves with it.
  useEffect(() => {
    ref.current?.focus()
  }, [])

  const columns: ReadonlyArray<Column<BeneficiaryLine>> = [
    { key: 'code', header: t('gateways.beneficiaries.identity'), render: (l) => <span className={styles.mono}>{l.beneficiary_code}</span> },
    {
      key: 'name',
      header: t('gateways.beneficiaries.name'),
      render: (l) => l.beneficiary_name || '—',
    },
    { key: 'count', header: t('gateways.beneficiaries.count'), numeric: true, render: (l) => String(l.count) },
    {
      key: 'holder',
      header: t('gateways.assignedTo'),
      render: (l) => (
        <SelectField
          label={t('gateways.assignedTo')}
          hideLabel
          value={l.biller_id}
          disabled={busy === l.beneficiary_code}
          onChange={(e) => onAssign(l, e.target.value)}
        >
          <option value="">{t('gateways.unassigned')}</option>
          {offeredFor(l).map((o) => (
            <option key={o.biller_id} value={o.biller_id}>
              {o.name}
            </option>
          ))}
        </SelectField>
      ),
    },
  ]

  return (
    <aside ref={ref} className={panel.panel} style={{ width: 'min(820px, 95vw)' }} role="region" aria-labelledby={headingId} tabIndex={-1}>
      <div className={panel.panelHeader}>
        <h2 className={panel.panelTitle} id={headingId}>
          {t('gateways.beneficiaries.title', { gateway: gateway.gateway_name })}
        </h2>
        <IconButton label={t('common.close')} size="sm" onClick={onClose}>
          ✕
        </IconButton>
      </div>
      <div className={panel.panelBody}>
        {data.isPending ? (
          <Skeleton variant="row" count={3} label={t('common.loading')} />
        ) : data.error ? (
          <EmptyState
            glyph="⚠"
            title={t('error.loadFailed')}
            body={apiErrorText(t, data.error).text}
            actions={<Button onClick={() => void data.refetch()}>{t('error.retry')}</Button>}
          />
        ) : (
          <>
            <p className={styles.noIdentity}>{t('gateways.beneficiaries.lead')}</p>
            <DataTable
              rows={data.data.beneficiaries}
              columns={columns}
              rowKey={(l) => l.beneficiary_code}
              sortLabel={(column) => column}
              caption={t('gateways.beneficiaries.title', { gateway: gateway.gateway_name })}
            />
            <p className={styles.noIdentity}>{t('gateways.beneficiaries.none', { count: data.data.without_identity })}</p>
          </>
        )}
      </div>
    </aside>
  )
}
